import { useCallback, useEffect, useState } from 'react';
import { clientFor, useAccounts } from '../auth/store';
import { useT } from '../i18n';
import { htmlToText, textToHtml } from '../mail/html';
import RichText from './RichText';

/*
    Who a message says it is from, and what it signs off with.

    There is one identity and it is the mailbox that signed in. Their mail server has no notion of
    send-as: a message is from whoever authenticated, the backend refuses to write any other From,
    and adding an address here would be offering something that could not work. So no list, no add,
    no remove — a display name and a signature.

    Both are kept in this browser, because there is nowhere on an IMAP server to put them. That is
    worth being plain about: set up on a second machine, they have to be set up again. The
    alternative would be writing a settings file into somebody's mailbox, which is not something a
    mail client should do to somebody's mail.

    Both halves of the signature are stored — the HTML one and a plain-text one — because every
    message goes out with both, and somebody reading the plain part should not get an empty
    sign-off.
*/

const card: React.CSSProperties = {
    background: 'var(--surface)',
    border: '1px solid var(--border)',
    borderRadius: 'var(--r-card)',
    padding: 16,
    display: 'grid',
    gap: 12,
};

const input: React.CSSProperties = {
    height: 30,
    padding: '0 10px',
    background: 'var(--surface2)',
    border: '1px solid var(--border)',
    borderRadius: 'var(--r-btn)',
    outline: 'none',
    fontSize: 'var(--fs-sm)',
    minWidth: 0,
    width: '100%',
};

function ghost(): React.CSSProperties {
    return {
        height: 27,
        padding: '0 11px',
        background: 'transparent',
        border: '1px solid var(--border2)',
        borderRadius: 'var(--r-btn)',
        color: 'var(--text-dim)',
        fontSize: 'var(--fs-sm)',
    };
}

export default function IdentitySettings() {
    const { t } = useT();
    const accounts = useAccounts((s) => s.accounts);
    const activeId = useAccounts((s) => s.activeId);
    const account = accounts.find((a) => a.id === activeId) ?? accounts[0];
    const client = clientFor(account?.id);

    const [name, setName] = useState('');
    const [html, setHtml] = useState('');
    const [text, setText] = useState('');
    const [showPlain, setShowPlain] = useState(false);
    // Bumped to reload the editor, which is uncontrolled: setting its content on every keystroke
    // would put the caret back at the start.
    const [seed, setSeed] = useState(0);
    const [saved, setSaved] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [loaded, setLoaded] = useState(false);
    // True once the plain text has been edited by hand, after which it stops following the HTML.
    const [plainEdited, setPlainEdited] = useState(false);

    /*
        The address, not the account object. The effect is about one identity and an identity is
        its address; depending on the object would re-run it whenever the account was re-created
        with the same email, and depending on `account?.email` while READING `account.email` left
        the rule unable to see that they are the same thing.
    */
    const address = account?.email;
    useEffect(() => {
        if (!address) return;
        let cancelled = false;
        // Asked of the ACCOUNT now, not of this browser — so the signature is the same one on
        // every computer the person signs in from.
        void client
            .identity(address)
            .then((identity) => {
                if (cancelled) return;
                setLoaded(true);
                setName(identity.name);
                setHtml(identity.htmlSignature);
                setText(identity.textSignature);
                setPlainEdited(Boolean(identity.textSignature) && identity.textSignature !== htmlToText(identity.htmlSignature));
                setSeed((n) => n + 1);
            })
            /*
                Okunamadıysa BOŞ bir editör gösterilmiyor ve kaydetme kapalı: bu ekranda boşluk,
                "imzan yok" demektir ve kişi yenisini yazıp gerçeğinin üstüne kaydeder.
            */
            .catch((e: unknown) => {
                if (cancelled) return;
                setError(e instanceof Error ? e.message : String(e));
            });
        return () => {
            cancelled = true;
        };
    }, [address, client]);

    const save = useCallback(() => {
        if (!account) return;
        setError(null);
        void client
            .saveIdentity(account.email, {
                name: name.trim(),
                htmlSignature: html,
                // Kept in step with the HTML unless somebody has taken it over.
                textSignature: plainEdited ? text : htmlToText(html),
            })
            .then(() => {
                setSaved(true);
                window.setTimeout(() => setSaved(false), 1600);
            })
            /*
                A failed save says so. It used to be impossible to fail — the write went to this
                browser — but now it crosses to the mail server, and a "saved" that only means
                "the click was received" is the kind of green tick this codebase has paid for
                before.
            */
            .catch((e: unknown) => setError(e instanceof Error ? e.message : String(e)));
    }, [account, client, name, html, text, plainEdited]);

    if (!account) {
        return <div style={card}>{t('identity.noAccount')}</div>;
    }

    return (
        <div style={{ display: 'grid', gap: 12 }}>
            <div style={card}>
                <div style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}>
                    <div style={{ fontSize: 'var(--fs-md)', fontWeight: 600 }}>{account.email}</div>
                    <div className="kicker">// {t('identity.mailbox')}</div>
                </div>

                <label style={{ display: 'grid', gap: 5 }}>
                    <span style={{ fontSize: 'var(--fs-sm)', color: 'var(--text-dim)' }}>{t('identity.displayName')}</span>
                    <input
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        placeholder={account.email}
                        style={input}
                        data-testid="identity-name"
                    />
                </label>

                <div className="kicker" style={{ lineHeight: 1.5 }}>{t('identity.localHint')}</div>

                {error ? (
                    <div data-testid="identity-error" style={{ fontSize: 'var(--fs-sm)', color: 'var(--red)' }}>
                        {error}
                    </div>
                ) : null}
            </div>

            <div style={card}>
                <div style={{ fontSize: 'var(--fs-md)', fontWeight: 600 }}>{t('identity.signature')}</div>

                <RichText
                    key={seed}
                    seed={String(seed)}
                    value={html}
                    onChange={(next) => {
                        setHtml(next);
                        if (!plainEdited) setText(htmlToText(next));
                    }}
                    minHeight={120}
                />

                <div className="kicker" style={{ lineHeight: 1.5 }}>{t('identity.logoHint')}</div>

                <div>
                    <button type="button" onClick={() => setShowPlain((v) => !v)} style={ghost()}>
                        {showPlain ? t('identity.hidePlain') : t('identity.editPlain')}
                    </button>
                </div>

                {showPlain ? (
                    <label style={{ display: 'grid', gap: 5 }}>
                        <span style={{ fontSize: 'var(--fs-sm)', color: 'var(--text-dim)' }}>{t('identity.plainSignature')}</span>
                        <textarea
                            value={text}
                            onChange={(e) => {
                                setText(e.target.value);
                                setPlainEdited(true);
                            }}
                            rows={5}
                            style={{ ...input, height: 'auto', padding: 9, fontFamily: 'var(--font-mono)', resize: 'vertical' }}
                        />
                        <span className="kicker" style={{ lineHeight: 1.5 }}>{t('identity.plainHint')}</span>
                    </label>
                ) : null}
            </div>

            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <button
                    type="button"
                    onClick={save}
                    style={{
                        height: 30,
                        padding: '0 14px',
                        background: 'var(--accent)',
                        color: 'var(--on-accent)',
                        border: 'none',
                        borderRadius: 'var(--r-btn)',
                        fontSize: 'var(--fs-sm)',
                        fontWeight: 600,
                    }}
                    data-testid="identity-save"
                    disabled={!loaded}
                >
                    {t('identity.save')}
                </button>
                {saved ? <span data-testid="identity-saved" className="kicker">// {t('identity.saved')}</span> : null}
            </div>
        </div>
    );
}

/** Kept so the composer can still ask for a preview of the signature as plain text. */
export function signaturePreview(html: string): string {
    return htmlToText(html) || textToHtml('');
}
